import axios from "axios";
import { API_BASE_URL } from "@/lib/constants";

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  // ``withCredentials`` is the SPA-wide opt-in for sending the
  // ``access_token`` / ``refresh_token`` / ``csrf_token`` cookies on
  // cross-origin requests (app.* → api.*). Without it the browser
  // strips the cookies and every authed request 401s.
  withCredentials: true,
});

// ── Phase 9: XSS-hardening + iOS Safari ITP rescue ───────────────
//
// Access tokens USED TO live in ``localStorage.access_token``, which
// made them XSS-readable. Phase 9 moved them to an httpOnly cookie
// set by the backend; the browser attaches the cookie automatically
// on cross-site requests when ``SameSite=None;Secure`` is set on the
// cookie AND ``withCredentials: true`` is on the client.
//
// That works on Chrome/Edge/Firefox but BREAKS on iOS Safari (and
// occasionally on Brave) because Intelligent Tracking Prevention
// drops or partitions third-party cookies set on a different domain
// from the SPA. Symptom: user clicks Sign in on mobile, the form
// shows a spinner, then the SPA bounces straight back to /login
// because the cookie that was just issued never made it into
// storage. Users can't sign in on iPhones at all.
//
// Fix: keep the cookie path as the PRIMARY auth, but also stash the
// access token in **module-scope memory** as a fallback. The axios
// interceptor + raw-fetch callers attach ``Authorization: Bearer``
// when the in-memory token is present. This restores iOS sign-in
// without re-opening the original XSS hole — module-scope is NOT
// reachable by an injected script the way ``localStorage`` is. An
// attacker would need arbitrary JS execution INSIDE the SPA bundle
// to read it, at which point they can do anything anyway.
//
// We intentionally do NOT write the token back to ``localStorage``
// — that's the vector Phase 9 closed.

let _accessTokenInMemory: string | null = null;
type AccessTokenListener = (token: string | null) => void;
const _accessTokenListeners = new Set<AccessTokenListener>();

export function setAccessToken(token: string | null) {
  // Belt-and-suspenders: scrub any legacy localStorage value left
  // over from the pre-Phase-9 days. We never write to localStorage
  // ourselves — only memory.
  try {
    localStorage.removeItem("access_token");
  } catch {
    /* storage blocked — ignore */
  }
  _accessTokenInMemory = token;
  for (const cb of _accessTokenListeners) {
    try {
      cb(token);
    } catch {
      /* listener crashed — keep others firing */
    }
  }
}

/**
 * Subscribe to access-token changes. Returns an unsubscribe function.
 * The auth context uses this to clear React state when the token is
 * wiped from another tab / 401 interceptor.
 */
export function subscribeAccessToken(cb: AccessTokenListener): () => void {
  _accessTokenListeners.add(cb);
  return () => {
    _accessTokenListeners.delete(cb);
  };
}

/**
 * Return the in-memory access token, or null when the SPA has none.
 * Cookie-only sessions (the happy desktop path) leave this null —
 * the bearer header is only attached when iOS Safari has dropped the
 * cookie AND login left a token here for us.
 */
export function getAccessToken(): string | null {
  return _accessTokenInMemory;
}

/**
 * Read a cookie by name. The backend issues ``csrf_token`` as a non-httpOnly
 * cookie specifically so this code can pick it up for the double-submit
 * header check. WORKS only for same-origin deploys — see ``getCsrfToken``
 * for the cross-origin path.
 */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split("; ") : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

// ── Cross-origin CSRF token storage ─────────────────────────────
//
// On a same-origin deploy (everything on lenavio.com) the SPA can
// read the ``csrf_token`` cookie directly via ``document.cookie``
// because the cookie's origin matches the SPA's origin.
//
// On a cross-origin deploy (app.lenavio.com → lenavio.onrender.com)
// the cookie is set on the API's origin and the SPA literally cannot
// see it from ``document.cookie``. The double-submit-cookie pattern
// breaks unless the SPA learns the token through a side channel.
//
// Solution: ``/auth/csrf`` returns the token in the JSON body. We
// cache the value in module-scoped state and use it as the
// ``X-CSRF-Token`` header. The browser still attaches the cookie
// automatically with ``withCredentials: true``, so the server's
// double-submit comparison succeeds. The in-memory copy IS readable
// by XSS — but a CSRF token is only useful with the matching cookie,
// which is httpOnly-equivalent for the attacker (different origin),
// so this trade-off keeps CSRF protection working without weakening
// the XSS hardening from the access-token migration.
let _csrfTokenInMemory: string | null = null;
let _csrfFetchInFlight: Promise<string | null> | null = null;

export function setCsrfTokenInMemory(token: string | null) {
  _csrfTokenInMemory = token;
}

export function getCsrfToken(): string | null {
  // Prefer the in-memory copy (works cross-origin); fall back to
  // ``document.cookie`` for same-origin deploys + first paint.
  return _csrfTokenInMemory || readCookie("csrf_token");
}

// Exported so non-axios callers (the NDJSON streaming command bar,
// any future EventSource/WebSocket wrapper that needs to pre-attach
// a CSRF header) can guarantee the token is fetched before they
// fire. The sync ``getCsrfToken`` accessor returns null when the
// in-memory cache is empty AND we're on a cross-origin deploy —
// callers that can afford an await should use this one to avoid
// races on first paint.
export async function ensureCsrfToken(): Promise<string | null> {
  if (_csrfTokenInMemory) return _csrfTokenInMemory;
  const fromCookie = readCookie("csrf_token");
  if (fromCookie) {
    _csrfTokenInMemory = fromCookie;
    return fromCookie;
  }
  if (_csrfFetchInFlight) return _csrfFetchInFlight;
  _csrfFetchInFlight = (async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/csrf`, {
        withCredentials: true,
      });
      const tok = res.data?.csrf_token as string | undefined;
      if (tok) _csrfTokenInMemory = tok;
      return _csrfTokenInMemory;
    } catch {
      return null;
    } finally {
      setTimeout(() => {
        _csrfFetchInFlight = null;
      }, 0);
    }
  })();
  return _csrfFetchInFlight;
}

const STATE_CHANGING = new Set(["post", "put", "patch", "delete"]);

// Request interceptor.
//
// Two headers can land here:
//
//   1. ``X-CSRF-Token`` on state-changing requests — required by the
//      global CSRF middleware. Fetched / cached by ``ensureCsrfToken``.
//
//   2. ``Authorization: Bearer <token>`` when the SPA has an
//      in-memory access token (post-login fallback for browsers that
//      ATE the cross-origin httpOnly cookie — chiefly iOS Safari ITP).
//      Cookie auth remains primary on every browser; the bearer
//      header is purely a redundancy that lets the SPA keep working
//      when the cookie path fails. Server side: when a Bearer
//      header is present the CSRF middleware skips its check (Bearer
//      tokens aren't CSRF-attackable — the browser doesn't auto-
//      attach Authorization), so this composes correctly with the
//      double-submit check.
client.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();

  // Bearer fallback for ITP-cookie-eating browsers.
  const bearer = getAccessToken();
  if (bearer && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${bearer}`;
  }

  if (STATE_CHANGING.has(method)) {
    // ``ensureCsrfToken`` fetches /auth/csrf on first call if
    // there's no in-memory or cookie value yet, then caches the
    // result. Awaiting here serialises the very first state-changing
    // request behind the CSRF bootstrap so the header is always
    // populated when the API needs it.
    const csrf = await ensureCsrfToken();
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }

  // Multipart uploads — let the browser set the Content-Type header
  // (which includes the multipart boundary) by deleting the default
  // ``application/json`` we set on ``axios.create``. Without this, the
  // server sees ``Content-Type: application/json`` for a FormData
  // body and the file part never gets parsed — surfaces as
  // "Field required (body.file)" on the CSV import endpoints.
  if (
    typeof FormData !== "undefined" &&
    config.data instanceof FormData &&
    config.headers
  ) {
    // Axios honors a single ``undefined`` to mean "let the platform
    // pick" (it actually deletes the header internally). Cover both
    // cases — common header name + the lowercase variant — because
    // the instance default sits on the canonical-cased name.
    if (
      "Content-Type" in (config.headers as Record<string, unknown>)
    ) {
      delete (config.headers as Record<string, unknown>)["Content-Type"];
    }
    if (
      "content-type" in (config.headers as Record<string, unknown>)
    ) {
      delete (config.headers as Record<string, unknown>)["content-type"];
    }
  }

  return config;
});

/**
 * Hard-wipe client-side auth state. Clears the access token, the CSRF cookie,
 * and the refresh_token cookie so the next page load starts from zero.
 *
 * We can only delete a cookie by setting it to an expired value on the same
 * path it was issued on. The refresh_token cookie is issued httpOnly, so we
 * can't actually erase its value from JS — but we can expire our copy so the
 * browser stops sending it. If the browser refuses (httpOnly lock), the
 * server-side /auth/logout call handles it; we fire that too on best-effort.
 */
function hardResetAuthState() {
  try {
    localStorage.removeItem("access_token");
  } catch {
    /* storage blocked — ignore */
  }
  // Clear the in-memory bearer fallback too — refresh failed, the
  // user is logged out, the token in memory is now invalid.
  // Notifies subscribers (AuthContext) so the SPA state wipes
  // through the same code path either auth source would trigger.
  setAccessToken(null);
  const expire = "; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  document.cookie = `csrf_token=${expire}`;
  document.cookie = `refresh_token=${expire}`;
}

/**
 * Response interceptor — silent refresh on 401.
 *
 * Access tokens expire after 24 hours (see ACCESS_TOKEN_EXPIRE_MINUTES).
 * Rather than booting the user to /login on every expiry, we:
 *   1. Catch a 401 from any request
 *   2. Call POST /auth/refresh (which reads the httpOnly refresh_token cookie)
 *   3. If that succeeds, write the new access token to localStorage and
 *      retry the original request with the fresh token
 *   4. If refresh itself 401s, THEN we hard-reset and redirect to /login
 *
 * Concurrent 401s share a single in-flight refresh promise so we don't
 * stampede the refresh endpoint if many requests expire at the same time.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  // The refresh endpoint sets a fresh access_token COOKIE *and*
  // returns the new token in the JSON body. We capture the body
  // copy into the in-memory bearer fallback so iOS Safari ITP
  // (which drops the cookie) can still authenticate the retried
  // original request via the Authorization header. Desktop browsers
  // pick up the cookie automatically and don't need the bearer —
  // but storing it costs nothing and the request interceptor
  // attaches it harmlessly alongside the auto-attached cookie.
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // Use the cross-origin-aware accessor — in cross-origin
      // deploys ``document.cookie`` doesn't expose the csrf_token
      // cookie, so we read from the in-memory copy that
      // ``ensureCsrfToken`` populated from /auth/csrf's body.
      const csrf = await ensureCsrfToken();
      const res = await axios.post<{ access_token?: string }>(
        `${API_BASE_URL}/auth/refresh`,
        {},
        {
          withCredentials: true,
          headers: csrf ? { "X-CSRF-Token": csrf } : {},
        }
      );
      const fresh = res?.data?.access_token;
      if (typeof fresh === "string" && fresh.length > 0) {
        setAccessToken(fresh);
      }
      return res.status >= 200 && res.status < 300;
    } catch {
      return false;
    } finally {
      // Clear shortly after so parallel 401s in the same tick share the result
      // but subsequent real failures will attempt a fresh refresh.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

// Paths where a 401 means "no session at all" rather than "session
// expired" — those routes intentionally return 401 to unauthenticated
// callers and the SPA shouldn't try to refresh in response (refresh
// would itself fail and trigger ``bootToLogin``, creating a redirect
// loop when the user is already on /login).
const _NO_REFRESH_PATHS = ["/auth/me"];

let redirecting = false;
function bootToLogin() {
  if (redirecting) return;
  // Already on a public auth page → no need to redirect. Otherwise
  // we get an infinite loop: /login renders → API call 401s →
  // refresh 403s → bootToLogin → window.location.replace("/login")
  // → /login re-renders → API call 401s → …
  try {
    const path = window.location.pathname;
    if (
      path === "/login" ||
      path === "/signup" ||
      path === "/register" ||
      path.startsWith("/sso") ||
      path.startsWith("/forgot-password") ||
      path.startsWith("/reset-password")
    ) {
      return;
    }
  } catch {
    /* SSR or unusual env — fall through and redirect */
  }
  redirecting = true;
  hardResetAuthState();
  window.dispatchEvent(new Event("auth:logout"));
  try {
    void fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
  window.location.replace("/login");
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config;

    // Don't try to refresh the refresh call itself — that way lies infinite loops.
    const isRefreshCall =
      typeof original?.url === "string" && original.url.includes("/auth/refresh");
    const isLoginCall =
      typeof original?.url === "string" && original.url.includes("/auth/login");
    // ``/auth/me`` returning 401 means "user is not logged in" — NOT
    // "access token expired". Triggering refresh would hit /auth/refresh,
    // which also 401s for an unauthenticated user, which would then
    // trigger ``bootToLogin`` → redirect loop on /login. Treat /me as
    // a pure auth probe and let the AuthContext catch handler decide.
    const isNoRefreshPath =
      typeof original?.url === "string" &&
      _NO_REFRESH_PATHS.some((p) => original.url.includes(p));

    // Demo-mode write guard rejection — surface as a distinct event so the
    // NotificationProvider can render a conversion-oriented toast instead of
    // a generic error. The backend responds with:
    //   { detail: { error: "demo_restricted" | "demo_ai_limit", message, signup_url } }
    if (status === 403) {
      const detail = error.response?.data?.detail;
      const code = detail?.error;
      if (code === "demo_restricted" || code === "demo_ai_limit") {
        window.dispatchEvent(
          new CustomEvent("demo:blocked", {
            detail: {
              code,
              message: detail.message,
              signupUrl: detail.signup_url,
              limit: detail.limit,
            },
          })
        );
        // Don't refresh — the 403 is intentional.
        return Promise.reject(error);
      }
    }

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isRefreshCall &&
      !isLoginCall &&
      !isNoRefreshPath
    ) {
      original._retried = true;
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Replay the original request. The refreshed access_token
        // cookie is auto-attached by the browser thanks to
        // ``withCredentials`` — no header juggling required.
        return client(original);
      }
      // Refresh failed → genuine logout.
      bootToLogin();
    }
    return Promise.reject(error);
  }
);

export default client;
